<template>
  <div ref="pageRoot" class="club-detail-page">
    <div v-if="loading" class="club-detail-loading">Загружаем клуб...</div>

    <div v-else-if="!club" class="club-detail-empty">
      <h1>Клуб не найден</h1>
      <button type="button" class="club-detail-empty__button" @click="$router.push('/clubs')">
        Вернуться к карте
      </button>
    </div>

    <template v-else>
      <section class="club-detail-hero">
        <div
          class="club-detail-gallery"
          @touchstart="onGalleryTouchStart"
          @touchmove="onGalleryTouchMove"
          @touchend="onGalleryTouchEnd"
          @touchcancel="onGalleryTouchEnd"
        >
          <template v-if="galleryImages.length">
            <transition name="club-gallery-fade" mode="out-in">
              <img
                :key="currentImage"
                :src="currentImage"
                :alt="club.name"
                class="club-detail-gallery__image"
              />
            </transition>
          </template>

          <template v-else>
            <div class="club-detail-gallery__placeholder">
              <span>{{ clubMonogram(club) }}</span>
            </div>
          </template>

          <div class="club-detail-gallery__scrim">
            <button
              v-if="galleryImages.length > 1"
              type="button"
              class="club-detail-gallery__nav club-detail-gallery__nav--prev"
              @click.stop="prevImage"
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M15 5 8 12l7 7" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            <button
              v-if="galleryImages.length > 1"
              type="button"
              class="club-detail-gallery__nav club-detail-gallery__nav--next"
              @click.stop="nextImage"
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="m9 5 7 7-7 7" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>

          <div class="club-detail-gallery__topbar">
            <button type="button" class="club-detail-gallery__icon-button" @click="goBack">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M15 5 8 12l7 7" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            <div class="club-detail-gallery__topbar-actions">
              <button type="button" class="club-detail-gallery__icon-button" @click="shareClub">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="18" cy="5" r="2.4" stroke="currentColor" stroke-width="2"/>
                  <circle cx="6" cy="12" r="2.4" stroke="currentColor" stroke-width="2"/>
                  <circle cx="18" cy="19" r="2.4" stroke="currentColor" stroke-width="2"/>
                  <path d="M8.2 10.9 15.4 6.9M8.2 13.1l7.2 4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>

              <button
                type="button"
                class="club-detail-gallery__icon-button"
                :class="{ 'is-active': isSaved }"
                @click="toggleSaved"
              >
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M7 4.5h10a1.5 1.5 0 0 1 1.5 1.5V20l-6.5-3.8L5.5 20V6A1.5 1.5 0 0 1 7 4.5Z"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div v-if="galleryImages.length" class="club-detail-gallery__counter">
            {{ currentImageIndex + 1 }}/{{ galleryImages.length }}
          </div>

          <div v-if="galleryImages.length > 1" class="club-detail-gallery__pager">
            <button
              v-for="(image, index) in galleryImages"
              :key="`${image}-${index}`"
              type="button"
              class="club-detail-gallery__dot"
              :class="{ 'is-active': index === currentImageIndex }"
              @click="goToImage(index)"
            ></button>
          </div>
        </div>
      </section>

      <section class="club-detail-content">
        <div class="club-detail-card">
          <div class="club-detail-content__head">
            <h1>{{ club.name }}</h1>

            <div class="club-detail-content__meta">
              <div class="club-detail-meta-row">
                <span class="club-detail-meta-icon">★</span>
                <strong>{{ displayRating(club) || '5.0' }}</strong>
                <span v-if="Number(club.reviews_count || 0) > 0">{{ club.reviews_count }}</span>
              </div>

              <div class="club-detail-meta-row club-detail-meta-row--soft">
                <span class="club-detail-meta-icon">
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M12 21s6-5.38 6-11a6 6 0 1 0-12 0c0 5.62 6 11 6 11Z" fill="currentColor"/>
                  </svg>
                </span>
                <span>{{ club.address || 'Адрес уточняется' }}</span>
              </div>
            </div>
          </div>

          <div class="club-detail-divider"></div>

          <section class="club-detail-section">
            <h2>Описание</h2>

            <div class="club-detail-description">
              <p v-for="(paragraph, index) in descriptionParagraphs" :key="index">
                {{ paragraph }}
              </p>
            </div>

            <div class="club-detail-facts">
              <div v-if="club.city" class="club-detail-fact-chip">{{ club.city }}</div>
              <div v-if="club.work_time_text" class="club-detail-fact-chip">{{ club.work_time_text }}</div>
              <div v-if="club.support_phone" class="club-detail-fact-chip">{{ club.support_phone }}</div>
            </div>
          </section>
        </div>
      </section>

      <teleport to="body">
        <div class="club-detail-booking-bar">
          <div class="club-detail-booking-bar__price-block">
            <span class="club-detail-booking-bar__price-label">За&nbsp;час</span>
            <strong>от {{ money(club.min_price_from || 0) }} ₽</strong>
          </div>

          <button type="button" class="club-detail-booking-bar__button" @click="goToBook">
            Забронировать
          </button>
        </div>
      </teleport>
    </template>
  </div>
</template>

<script>
export default {
  name: 'ClubDetailsView',

  data() {
    return {
      loading: false,
      currentImageIndex: 0,
      isSaved: false,
      layoutLockElements: [],
      galleryTouchStartX: 0,
      galleryTouchCurrentX: 0,
      galleryTouchActive: false,
    };
  },

  computed: {
    clubId() {
      return Number(this.$route.params.clubId || 0);
    },

    club() {
      return this.$store.getters['clubs/clubById'](this.clubId);
    },

    minHourlyPrice() {
      return Number(this.club?.min_price_from || 0);
    },

    galleryImages() {
      if (!this.club) return [];

      const fromServer = Array.isArray(this.club.gallery_images)
        ? this.club.gallery_images.map((item) => item.image_url).filter(Boolean)
        : [];

      const extra = [
        this.club.cover_image_url,
        this.club.image_url,
      ].filter(Boolean);

      return Array.from(new Set([...fromServer, ...extra]));
    },

    currentImage() {
      return this.galleryImages[this.currentImageIndex] || '';
    },

    descriptionParagraphs() {
      const directText = [
        this.club?.description_full,
        this.club?.description,
        this.club?.short_description,
      ].find((item) => String(item || '').trim());

      if (directText) {
        return String(directText)
          .split(/\n+/)
          .map((item) => item.trim())
          .filter(Boolean);
      }

      const generated = [];

      if (this.club?.work_time_text) {
        generated.push(`Режим работы: ${this.club.work_time_text}.`);
      }

      if (this.club?.support_phone) {
        generated.push(`Контактный телефон: ${this.club.support_phone}.`);
      }

      generated.push('Современный компьютерный клуб сети с комфортной посадкой, мощными ПК и атмосферой для игр, стриминга и командных сессий.');

      return generated;
    },
  },

  watch: {
    club() {
      this.currentImageIndex = 0;
      this.syncDocumentTitle();
      this.loadSavedState();
    },

    clubId: {
      immediate: false,
      async handler() {
        await this.ensureClubLoaded();
      },
    },
  },

  async mounted() {
    this.applyLayoutLocks();
    await this.ensureClubLoaded();
    this.syncDocumentTitle();
    this.loadSavedState();
  },

  beforeUnmount() {
    this.removeLayoutLocks();
  },

  methods: {
    async ensureClubLoaded() {
      if (!this.clubId) return;

      this.loading = true;
      try {
        await this.$store.dispatch('clubs/fetchClubById', this.clubId);
      } finally {
        this.loading = false;
      }
    },

    applyLayoutLocks() {
      const elements = [];
      let node = this.$refs.pageRoot;

      while (node && node.parentElement) {
        node = node.parentElement;
        const cls = typeof node.className === 'string' ? node.className : '';

        if (
          cls.includes('app-shell') ||
          cls.includes('app-page') ||
          cls.includes('app-content') ||
          cls.includes('page-content')
        ) {
          node.classList.add('club-detail-fullbleed');
          elements.push(node);
        }
      }

      this.layoutLockElements = elements;
    },

    removeLayoutLocks() {
      (this.layoutLockElements || []).forEach((el) => {
        el.classList.remove('club-detail-fullbleed');
      });
      this.layoutLockElements = [];
    },

    syncDocumentTitle() {
      if (this.club?.name) {
        document.title = `${this.club.name} — Компьютерный клуб`;
      }
    },

    displayRating(club) {
      const rating = Number(club?.rating || 0);
      if (!Number.isFinite(rating) || rating <= 0) return '';
      return rating.toFixed(1);
    },

    money(value) {
      const amount = Number(value || 0);
      return new Intl.NumberFormat('ru-RU').format(amount);
    },

    clubMonogram(club) {
      const name = String(club?.name || '').trim();
      if (!name) return 'CC';

      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

      return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
    },

    storageKey() {
      return `club_saved_${this.clubId}`;
    },

    loadSavedState() {
      try {
        this.isSaved = localStorage.getItem(this.storageKey()) === '1';
      } catch (error) {
        this.isSaved = false;
      }
    },

    toggleSaved() {
      this.isSaved = !this.isSaved;
      try {
        localStorage.setItem(this.storageKey(), this.isSaved ? '1' : '0');
      } catch (error) {
        // ignore
      }
    },

    prevImage() {
      if (this.galleryImages.length <= 1) return;
      this.currentImageIndex =
        (this.currentImageIndex - 1 + this.galleryImages.length) % this.galleryImages.length;
    },

    nextImage() {
      if (this.galleryImages.length <= 1) return;
      this.currentImageIndex = (this.currentImageIndex + 1) % this.galleryImages.length;
    },

    goToImage(index) {
      if (index < 0 || index >= this.galleryImages.length) return;
      this.currentImageIndex = index;
    },

    onGalleryTouchStart(event) {
      if (this.galleryImages.length <= 1) return;
      const touch = event.touches?.[0];
      if (!touch) return;

      this.galleryTouchActive = true;
      this.galleryTouchStartX = touch.clientX;
      this.galleryTouchCurrentX = touch.clientX;
    },

    onGalleryTouchMove(event) {
      if (!this.galleryTouchActive || this.galleryImages.length <= 1) return;
      const touch = event.touches?.[0];
      if (!touch) return;
      this.galleryTouchCurrentX = touch.clientX;
    },

    onGalleryTouchEnd() {
      if (!this.galleryTouchActive || this.galleryImages.length <= 1) {
        this.galleryTouchActive = false;
        return;
      }

      const deltaX = this.galleryTouchCurrentX - this.galleryTouchStartX;

      if (Math.abs(deltaX) >= 48) {
        if (deltaX > 0) {
          this.prevImage();
        } else {
          this.nextImage();
        }
      }

      this.galleryTouchActive = false;
      this.galleryTouchStartX = 0;
      this.galleryTouchCurrentX = 0;
    },

    async goToBook() {
      if (!this.club) return;

      await this.$store.dispatch('clubs/selectClub', Number(this.club.id));
      this.$router.push('/book');
    },

    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push('/clubs');
      }
    },

    async shareClub() {
      const shareData = {
        title: this.club?.name || 'Клуб',
        text: `${this.club?.name || 'Клуб'} — ${this.club?.address || ''}`.trim(),
        url: window.location.href,
      };

      if (navigator.share) {
        try {
          await navigator.share(shareData);
          return;
        } catch (error) {
          if (error?.name === 'AbortError') return;
        }
      }

      try {
        await navigator.clipboard.writeText(shareData.url);
      } catch (error) {
        // ignore
      }
    },
  },
};
</script>

<style src="./ClubDetailsView.css"></style>